// BookingManagement: Admin approve/reject, assign staff, manual ticket modal, send email, search/filter/pagination

import React, { useEffect, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import SuperAdminLayout from '../../layouts/SuperAdminLayout';
import { Box, Typography, Card, CardContent, Button, Grid, IconButton, Modal, Divider, Tooltip, TextField } from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import api from '../../services/api';
import { useProfile } from '../../context/ProfileContext';
import NotificationSnackbar from '../../components/NotificationSnackbar';
import PrintPreviewModal from '../../components/PrintPreviewModal';
import TicketDesignModal, { TicketPreview } from '../../components/TicketDesignModal';
import BookingModal from '../../components/BookingModal';


function BookingManagement() {
    // Payment request email dialog state
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [paymentEmailLoading, setPaymentEmailLoading] = useState(false);
    const [paymentEmailResult, setPaymentEmailResult] = useState('');
    const [paymentEmail, setPaymentEmail] = useState('');
    const [paymentMsg, setPaymentMsg] = useState('');
    const [offlinePaymentDetails, setOfflinePaymentDetails] = useState('');
    // Fetch offline payment details from settings on mount
    useEffect(() => {
      api.get('/api/settings').then(res => {
        setOfflinePaymentDetails(res.data.data?.offlinePaymentDetails || '');
      });
    }, []);
    // Send payment request email handler
    const handleSendPaymentRequest = async () => {
      if (!selectedBooking) return;
      setPaymentEmailLoading(true);
      setPaymentEmailResult('');
      try {
        // Compose message: use offlinePaymentDetails and booking info
        const email = selectedBooking.userId?.email || selectedBooking.bookingDetails?.email;
        const name = selectedBooking.userId?.name || selectedBooking.bookingDetails?.bookerName || 'User';
        const packageName = selectedBooking.packageId?.name || selectedBooking.bookingDetails?.packageName || '';
        const amount = selectedBooking.packageId?.price || '';
        const msg = paymentMsg || offlinePaymentDetails || '';
        const subject = `Payment Request: ${packageName}`;
        const html = `<p>Dear ${name},</p><p>Please complete your payment for <b>${packageName}</b>.<br/>Amount: <b>${amount}</b></p><div style='margin:16px 0;padding:12px 16px;background:#e6f0ff;border-radius:8px;color:#007bff;font-weight:600;'>${msg}</div><p>Thank you!</p>`;
        await api.post('/api/utils/send-payment-request', { to: email, subject, html });
        setPaymentEmailResult('Payment request email sent successfully!');
      } catch (err) {
        setPaymentEmailResult(err.response?.data?.error || 'Failed to send payment request email');
      } finally {
        setPaymentEmailLoading(false);
      }
    };
  // Bookings state must be declared before usage
  const [bookings, setBookings] = useState([]);
  // Filtering state
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Filtered bookings
  const filteredBookings = bookings.filter(b => {
    let typeMatch = filterType === 'all' || (b.packageId?.category || b.bookingDetails?.category || '').toLowerCase() === filterType;
    let statusMatch = filterStatus === 'all' || b.status === filterStatus;
    return typeMatch && statusMatch;
  });
        // Helper to get ISO country code from country name (supports all countries)
              // Helper to pick a design: prefer exact category, otherwise the first available design
              function pickDesign(designs, cat) {
                if (!designs || typeof designs !== 'object') return {};
                if (cat && designs[cat]) return designs[cat];
                const keys = Object.keys(designs || {});
                return keys.length ? designs[keys[0]] : {};
              }
        function getCountryCode(countryName) {
          if (!countryName) return '';
          // Use a robust lookup for all countries
          const countries = {
            'Afghanistan': 'AF', 'Albania': 'AL', 'Algeria': 'DZ', 'Andorra': 'AD', 'Angola': 'AO', 'Antigua and Barbuda': 'AG',
            'Argentina': 'AR', 'Armenia': 'AM', 'Australia': 'AU', 'Austria': 'AT', 'Azerbaijan': 'AZ', 'Bahamas': 'BS',
            'Bahrain': 'BH', 'Bangladesh': 'BD', 'Barbados': 'BB', 'Belarus': 'BY', 'Belgium': 'BE', 'Belize': 'BZ',
            'Benin': 'BJ', 'Bhutan': 'BT', 'Bolivia': 'BO', 'Bosnia and Herzegovina': 'BA', 'Botswana': 'BW', 'Brazil': 'BR',
            'Brunei': 'BN', 'Bulgaria': 'BG', 'Burkina Faso': 'BF', 'Burundi': 'BI', 'Cabo Verde': 'CV', 'Cambodia': 'KH',
            'Cameroon': 'CM', 'Canada': 'CA', 'Central African Republic': 'CF', 'Chad': 'TD', 'Chile': 'CL', 'China': 'CN',
            'Colombia': 'CO', 'Comoros': 'KM', 'Congo': 'CG', 'Costa Rica': 'CR', 'Croatia': 'HR', 'Cuba': 'CU',
            'Cyprus': 'CY', 'Czech Republic': 'CZ', 'Denmark': 'DK', 'Djibouti': 'DJ', 'Dominica': 'DM', 'Dominican Republic': 'DO',
            'Ecuador': 'EC', 'Egypt': 'EG', 'El Salvador': 'SV', 'Equatorial Guinea': 'GQ', 'Eritrea': 'ER', 'Estonia': 'EE',
            'Eswatini': 'SZ', 'Ethiopia': 'ET', 'Fiji': 'FJ', 'Finland': 'FI', 'France': 'FR', 'Gabon': 'GA', 'Gambia': 'GM',
            'Georgia': 'GE', 'Germany': 'DE', 'Ghana': 'GH', 'Greece': 'GR', 'Grenada': 'GD', 'Guatemala': 'GT', 'Guinea': 'GN',
            'Guinea-Bissau': 'GW', 'Guyana': 'GY', 'Haiti': 'HT', 'Honduras': 'HN', 'Hungary': 'HU', 'Iceland': 'IS',
            'India': 'IN', 'Indonesia': 'ID', 'Iran': 'IR', 'Iraq': 'IQ', 'Ireland': 'IE', 'Israel': 'IL', 'Italy': 'IT',
            'Jamaica': 'JM', 'Japan': 'JP', 'Jordan': 'JO', 'Kazakhstan': 'KZ', 'Kenya': 'KE', 'Kiribati': 'KI', 'Kuwait': 'KW',
            'Kyrgyzstan': 'KG', 'Laos': 'LA', 'Latvia': 'LV', 'Lebanon': 'LB', 'Lesotho': 'LS', 'Liberia': 'LR', 'Libya': 'LY',
            'Liechtenstein': 'LI', 'Lithuania': 'LT', 'Luxembourg': 'LU', 'Madagascar': 'MG', 'Malawi': 'MW', 'Malaysia': 'MY',
            'Maldives': 'MV', 'Mali': 'ML', 'Malta': 'MT', 'Marshall Islands': 'MH', 'Mauritania': 'MR', 'Mauritius': 'MU',
            'Mexico': 'MX', 'Micronesia': 'FM', 'Moldova': 'MD', 'Monaco': 'MC', 'Mongolia': 'MN', 'Montenegro': 'ME',
            'Morocco': 'MA', 'Mozambique': 'MZ', 'Myanmar': 'MM', 'Namibia': 'NA', 'Nauru': 'NR', 'Nepal': 'NP', 'Netherlands': 'NL',
            'New Zealand': 'NZ', 'Nicaragua': 'NI', 'Niger': 'NE', 'Nigeria': 'NG', 'North Korea': 'KP', 'North Macedonia': 'MK',
            'Norway': 'NO', 'Oman': 'OM', 'Pakistan': 'PK', 'Palau': 'PW', 'Palestine': 'PS', 'Panama': 'PA', 'Papua New Guinea': 'PG',
            'Paraguay': 'PY', 'Peru': 'PE', 'Philippines': 'PH', 'Poland': 'PL', 'Portugal': 'PT', 'Qatar': 'QA', 'Romania': 'RO',
            'Russia': 'RU', 'Rwanda': 'RW', 'Saint Kitts and Nevis': 'KN', 'Saint Lucia': 'LC', 'Saint Vincent and the Grenadines': 'VC',
            'Samoa': 'WS', 'San Marino': 'SM', 'Sao Tome and Principe': 'ST', 'Saudi Arabia': 'SA', 'Senegal': 'SN', 'Serbia': 'RS',
            'Seychelles': 'SC', 'Sierra Leone': 'SL', 'Singapore': 'SG', 'Slovakia': 'SK', 'Slovenia': 'SI', 'Solomon Islands': 'SB',
            'Somalia': 'SO', 'South Africa': 'ZA', 'South Korea': 'KR', 'South Sudan': 'SS', 'Spain': 'ES', 'Sri Lanka': 'LK',
            'Sudan': 'SD', 'Suriname': 'SR', 'Sweden': 'SE', 'Switzerland': 'CH', 'Syria': 'SY', 'Taiwan': 'TW', 'Tajikistan': 'TJ',
            'Tanzania': 'TZ', 'Thailand': 'TH', 'Timor-Leste': 'TL', 'Togo': 'TG', 'Tonga': 'TO', 'Trinidad and Tobago': 'TT',
            'Tunisia': 'TN', 'Turkey': 'TR', 'Turkmenistan': 'TM', 'Tuvalu': 'TV', 'Uganda': 'UG', 'Ukraine': 'UA', 'United Arab Emirates': 'AE',
            'United Kingdom': 'GB', 'United States': 'US', 'Uruguay': 'UY', 'Uzbekistan': 'UZ', 'Vanuatu': 'VU', 'Vatican City': 'VA',
            'Venezuela': 'VE', 'Vietnam': 'VN', 'Yemen': 'YE', 'Zambia': 'ZM', 'Zimbabwe': 'ZW'
          };
          // Try direct match, else try to match ignoring case and whitespace
          if (countries[countryName]) return countries[countryName];
          const normalized = countryName.trim().toLowerCase();
          for (const key in countries) {
            if (key.trim().toLowerCase() === normalized) return countries[key];
          }
          return '';
        }
    // Action handlers
    const handleApprove = async (bookingId) => {
      await api.post(`/api/bookings/${bookingId}/approve`);
      setBookings(prev => prev.map(b => b._id === bookingId ? { ...b, status: 'approved' } : b));
      if (selectedBooking && selectedBooking._id === bookingId) setSelectedBooking({ ...selectedBooking, status: 'approved' });
    };
    const handleReject = async (bookingId) => {
      await api.post(`/api/bookings/${bookingId}/reject`);
      setBookings(prev => prev.map(b => b._id === bookingId ? { ...b, status: 'rejected', paymentStatus: 'rejected' } : b));
      if (selectedBooking && selectedBooking._id === bookingId) setSelectedBooking({ ...selectedBooking, status: 'rejected', paymentStatus: 'rejected' });
    };
    // ...existing code...
    // Upload verification modal logic
    const [verificationOpen, setVerificationOpen] = useState(false);
    const [payDetail, setPayDetail] = useState("");
    const [verifying, setVerifying] = useState(false);
    const [verificationError, setVerificationError] = useState("");
    const handleUploadVerification = (bookingId) => {
      setPayDetail("");
      setVerificationError("");
      setVerificationOpen(true);
    };
    const handleVerificationSubmit = async () => {
      if (!payDetail) {
        setVerificationError("Payment detail is required.");
        return;
      }
      setVerifying(true);
      setVerificationError("");
      try {
        const res = await api.post(`/api/bookings/${selectedBooking._id}/upload-verification`, { paymentDetail: payDetail });
        setBookings(prev => prev.map(b => b._id === selectedBooking._id ? { ...b, paymentStatus: 'paid', bookingDetails: { ...b.bookingDetails, paymentDetail: payDetail } } : b));
        setSelectedBooking(s => s ? { ...s, paymentStatus: 'paid', bookingDetails: { ...s.bookingDetails, paymentDetail: payDetail } } : s);
        setSnackbar({ open: true, message: 'Payment marked as paid.', severity: 'success' });
        setVerificationOpen(false);
      } catch (err) {
        if (err.response && err.response.status === 403) {
          setVerificationError("You don't have permission for this!");
        } else {
          setVerificationError(err.response?.data?.error || 'Failed to mark as paid.');
        }
      } finally {
        setVerifying(false);
      }
    };
  const [showSpeakers, setShowSpeakers] = useState(false);
  const [ticketDesigns, setTicketDesigns] = useState({});
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [printPreviewData, setPrintPreviewData] = useState({ design: null, ticket: null });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [addOpen, setAddOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [modalOpen, setModalOpen] = useState(false); // For view details
  const [actionModalOpen, setActionModalOpen] = useState(false); // For actions
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { profile } = useProfile() || {};
  const currentUser = profile || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || '{}') : {});

  useEffect(() => {
    setLoading(true);
    // Always fetch all bookings, including paid
    api.get('/api/bookings')
      .then(res => setBookings(res.data))
      .catch(err => setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to fetch bookings', severity: 'error' }))
      .finally(() => setLoading(false));
    // load ticket designs for previews
    api.get('/api/ticket-designs').then(res => {
      if (res.data && typeof res.data === 'object') setTicketDesigns(res.data);
    }).catch(() => {});
  }, []);

  // Delete with confirmation dialog
  const handleDelete = async (bookingId) => {
    setConfirmDelete(false);
    setLoading(true);
    setSnackbar({ open: false, message: '', severity: 'info' });
    try {
      await api.delete(`/api/bookings/${bookingId}`);
      setBookings(prev => prev.filter(b => b._id !== bookingId));
      setSnackbar({ open: true, message: 'Booking deleted successfully', severity: 'success' });
      handleCloseModal && handleCloseModal();
    } catch (err) {
      if (err.response && err.response.status === 403) {
        setSnackbar({ open: true, message: "Don't have permission", severity: 'error' });
      } else {
        setSnackbar({ open: true, message: err.response?.data?.error || 'Delete failed', severity: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };
  // Document request dialog state and handler
  const [docRequestOpen, setDocRequestOpen] = useState(false);
  const [docRequestMsg, setDocRequestMsg] = useState('');
  const [docRequestLoading, setDocRequestLoading] = useState(false);
  const handleSendDocRequest = async () => {
    if (!selectedBooking) return;
    setDocRequestLoading(true);
    try {
      await api.post(`/api/bookings/${selectedBooking._id}/request-docs`, { message: docRequestMsg });
      setSnackbar({ open: true, message: 'Document request sent.', severity: 'success' });
      setDocRequestOpen(false);
      setDocRequestMsg('');
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to send document request', severity: 'error' });
    } finally {
      setDocRequestLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(s => ({ ...s, open: false }));
  };

  const handleAddBooking = () => setAddOpen(true);
  const handleAddClose = () => setAddOpen(false);
  const handleManualBooking = () => setManualOpen(true);
  const handleManualClose = () => setManualOpen(false);

  // Refresh bookings from API
  const refreshBookings = () => {
    setLoading(true);
    api.get('/api/bookings')
      .then(res => setBookings(res.data))
      .catch(err => setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to fetch bookings', severity: 'error' }))
      .finally(() => setLoading(false));
  };

  // Expose refresh function for other pages (e.g. PaymentsManagement) to call safely
  React.useEffect(() => {
    const handler = () => refreshBookings();
    window.addEventListener('bookings:refresh', handler);
    return () => {
      window.removeEventListener('bookings:refresh', handler);
    };
  }, []);

  // After add, refresh bookings
  const handleBookingAdded = () => {
    refreshBookings();
    setSnackbar({ open: true, message: 'Booking added successfully', severity: 'success' });
  };

  const handleOpenModal = (booking) => {
    setSelectedBooking(booking);
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedBooking(null);
  };

  return (
    <SuperAdminLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Booking Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button variant="contained" color="secondary" onClick={handleManualBooking}>Create Booking</Button>
          <Button variant={showSpeakers ? 'contained' : 'outlined'} color="primary" onClick={() => setShowSpeakers(s => !s)}>
            {showSpeakers ? 'Show All Bookings' : 'Show Speaker Bookings'}
          </Button>
        </Box>
        <BookingModal open={manualOpen} onClose={handleManualClose} onBookingAdded={handleBookingAdded} manual />
        {loading ? <Typography>Loading...</Typography> : (
          <Grid container spacing={2}>
            {['pending', 'approved', 'rejected'].map(statusCat => (
              <Grid item xs={12} key={statusCat}>
                <Typography variant="h5" sx={{ mt: 3, mb: 1, color: statusCat === 'approved' ? 'green' : statusCat === 'rejected' ? 'red' : '#007bff', fontWeight: 700 }}>
                  {statusCat.charAt(0).toUpperCase() + statusCat.slice(1)} Bookings
                </Typography>
                <Grid container spacing={2}>
                  {bookings.filter(b => b.status === statusCat).map((booking) => (
                    <Grid item xs={12} md={6} lg={4} key={booking._id}>
                      <Card variant="outlined" sx={{ mb: 1, borderColor: statusCat === 'approved' ? 'green' : statusCat === 'rejected' ? 'red' : '#007bff', boxShadow: statusCat === 'pending' ? '0 0 4px #b2f5ea' : undefined, minHeight: 90, maxWidth: 320, p: 0.5 }}>
                        <CardContent sx={{ p: 1.2, '&:last-child': { pb: 1.2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" fontWeight={700} color="primary.main" noWrap sx={{ fontSize: 15 }}>
                              {booking.bookingDetails?.bookerName || booking.bookingDetails?.companyDetails?.name || 'N/A'}
                            </Typography>
                            {booking.bookingDetails?.country && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                                <ReactCountryFlag
                                  countryCode={getCountryCode(booking.bookingDetails.country)}
                                  svg
                                  style={{ width: '2em', height: '2em', borderRadius: '4px', boxShadow: '0 0 4px #007bff', border: '2px solid #007bff', background: '#fff' }}
                                />
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#007bff', fontSize: 15, ml: 0.5 }}>{booking.bookingDetails.country}</Typography>
                              </Box>
                            )}
                            {/** Render a unified status icon (pending/approved/rejected) and show ticket marker when present */}
                            {(() => {
                              let statusIcon = <HourglassEmptyOutlinedIcon style={{ color: '#f0ad4e', fontSize: 18, marginLeft: 6 }} titleAccess="Pending" />;
                              if (booking.status === 'approved' && booking.paymentStatus === 'paid') {
                                statusIcon = <CheckCircleIcon style={{ color: '#388e3c', fontSize: 18, marginLeft: 6 }} titleAccess="Approved & Paid" />;
                              } else if (booking.status === 'rejected' || booking.paymentStatus === 'rejected') {
                                statusIcon = <CancelIcon style={{ color: '#d32f2f', fontSize: 18, marginLeft: 6 }} titleAccess="Rejected" />;
                              }
                              // Ticket created marker (green) if booking has ticketStatus
                              if (booking.ticketStatus === 'created' || booking.ticketStatus === 'ticketed' || booking.ticketStatus === 'issued') {
                                statusIcon = <ConfirmationNumberIcon style={{ color: '#2e7d32', fontSize: 18, marginLeft: 6 }} titleAccess="Ticket Created" />;
                              }
                              return statusIcon;
                            })()}
                          </Box>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: 13 }}>
                            <b>Email:</b> {booking.userId?.email || booking.bookingDetails?.email || 'N/A'}
                          </Typography>
                          <Typography variant="body2" noWrap sx={{ fontSize: 13 }}>
                            <b>Package:</b> {booking.packageId?.name || booking.bookingDetails?.packageName || 'N/A'} | <b>Category:</b> {booking.packageId?.category || booking.bookingDetails?.category || 'N/A'}
                          </Typography>
                          <Typography variant="body2" noWrap sx={{ fontSize: 13 }}>
                            <b>Company:</b> {booking.bookingDetails?.companyName || booking.bookingDetails?.companyDetails?.name || 'N/A'}
                          </Typography>
                          <Typography variant="body2" noWrap sx={{ fontSize: 13 }}>
                            <b>Mobile:</b> {booking.bookingDetails?.mobile || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <b>Date:</b> {new Date(booking.createdAt).toLocaleString()}
                          </Typography>
                          {booking.bookingDetails && (
                            <Box sx={{ mt: 1 }}>
                              {booking.bookingDetails.companyDetails?.hotelId && (
                                <Typography variant="body2"><b>Hotel:</b> {booking.bookingDetails.companyDetails.hotelId}</Typography>
                              )}
                              {booking.bookingDetails.companyDetails?.departureDate && (
                                <Typography variant="body2"><b>Departure Date:</b> {booking.bookingDetails.companyDetails.departureDate}</Typography>
                              )}
                              {booking.bookingDetails.companyDetails?.departureTime && (
                                <Typography variant="body2"><b>Departure Time:</b> {booking.bookingDetails.companyDetails.departureTime}</Typography>
                              )}
                              {booking.bookingDetails.speakerOptions && (
                                <Typography variant="body2"><b>Speaker Options:</b> {JSON.stringify(booking.bookingDetails.speakerOptions)}</Typography>
                              )}
                              {booking.bookingDetails.bulkCount && (
                                <Typography variant="body2"><b>Bulk Count:</b> {booking.bookingDetails.bulkCount}</Typography>
                              )}
                              {booking.bookingDetails.companyDetails?.docUrl && (
                                <Button size="small" sx={{ mt: 1 }} variant="contained" color="info" onClick={() => window.open(booking.bookingDetails.companyDetails.docUrl, '_blank')}>View Uploaded Document</Button>
                              )}
                            </Box>
                          )}
                          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Tooltip title="View Details">
                              <IconButton onClick={() => { setSelectedBooking(booking); setModalOpen(true); }} aria-label="View">
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Actions">
                              <IconButton onClick={() => { setSelectedBooking(booking); setActionModalOpen(true); }} aria-label="Actions">
                                <MoreVertIcon />
                              </IconButton>
                            </Tooltip>
                            {/* Only show ticket print icon if booking is eligible */}
                            {(booking.status === 'approved' && (booking.paymentStatus === 'paid' || booking.paymentStatus === 'verified')) && (
                              <Tooltip title="Preview Ticket">
                                <IconButton size="small" color="primary" onClick={async () => {
                                  // prepare preview ticket data
                                  const cat = booking.packageId?.category || booking.bookingDetails?.category || 'attendee';
                                  let designs = ticketDesigns;
                                  if (!designs || Object.keys(designs).length === 0) {
                                    try {
                                      const r = await api.get('/api/ticket-designs');
                                      designs = r.data || {};
                                      setTicketDesigns(designs);
                                    } catch (e) {
                                      designs = {};
                                    }
                                  }
                                  const design = pickDesign(designs, cat);
                                  const ticketForPreview = {
                                    type: cat,
                                    attendee: booking.bookingDetails?.bookerName || booking.bookingDetails?.companyDetails?.name || '',
                                    reference: booking.ticketReference || booking._id,
                                    status: booking.status,
                                    company: booking.bookingDetails?.companyDetails?.name || '',
                                    email: booking.userId?.email || booking.bookingDetails?.email || '',
                                    phone: booking.bookingDetails?.mobile || booking.bookingDetails?.phone || '',
                                    packageName: booking.packageId?.name || booking.bookingDetails?.packageName || '',
                                    peopleCount: booking.bookingDetails?.peopleCount
                                  };
                                  setPrintPreviewData({ design, ticket: ticketForPreview });
                                  setPrintPreviewOpen(true);
                                }}>
                                  <ConfirmationNumberIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* Action Modal for all actions - icon button style, professional */}
                            <Modal open={!!actionModalOpen && !!selectedBooking} onClose={() => setActionModalOpen(false)}>
                              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 3, minWidth: 320, maxWidth: 400, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: '#007bff', mb: 2 }}>Booking Actions</Typography>
                                <Divider sx={{ mb: 2, width: '100%' }} />
                                <Box sx={{ display: 'flex', gap: 3, mb: 2, justifyContent: 'center' }}>
                                  <Tooltip title="Approve">
                                    <span>
                                      <IconButton color="success" onClick={() => handleApprove(selectedBooking._id)} disabled={selectedBooking?.status === 'approved'} size="large">
                                        <CheckCircleIcon fontSize="inherit" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Reject">
                                    <IconButton color="error" onClick={() => handleReject(selectedBooking._id)} size="large">
                                      <CancelIcon fontSize="inherit" />
                                    </IconButton>
                                  </Tooltip>
                                  {/* Only superadmin or assigned admin can delete */}
                                  {((currentUser.role === 'superadmin') || (currentUser.role === 'admin' && selectedBooking?.assignedAdminId === currentUser._id)) && (
                                    <Tooltip title="Delete">
                                      <IconButton color="error" onClick={() => setConfirmDelete(true)} size="large">
                                        <DeleteIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                          {/* Delete confirmation dialog */}
                                          <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(false)}>
                                            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 3, minWidth: 320, maxWidth: 400, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                              <Typography variant="h6" fontWeight={700} gutterBottom color="error">Confirm Delete</Typography>
                                              <Divider sx={{ mb: 2, width: '100%' }} />
                                              <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>Are you sure you want to delete this booking? This action cannot be undone.</Typography>
                                              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                                <Button onClick={() => setConfirmDelete(false)} variant="outlined">Cancel</Button>
                                                <Button onClick={() => handleDelete(selectedBooking._id)} color="error" variant="contained">Delete</Button>
                                              </Box>
                                            </Box>
                                          </Modal>
                                  {!selectedBooking?.ticketStatus ? (
                                    <>
                                      <Tooltip title="Send Payment Request Email">
                                        <IconButton color="info" onClick={() => {
                                          setPaymentEmail(selectedBooking?.userId?.email || selectedBooking?.bookingDetails?.email || '');
                                          setPaymentMsg(offlinePaymentDetails);
                                          setPaymentDialogOpen(true);
                                        }} size="large">
                                          <EmailIcon fontSize="inherit" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Request Documents from Booker">
                                        <IconButton color="primary" onClick={() => { setDocRequestOpen(true); setDocRequestMsg('Please upload the required documents to complete your booking.'); }} size="large">
                                          <CloudUploadIcon fontSize="inherit" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  ) : (
                                    // If ticket already created, show ticket send/print actions instead of payment actions
                                    <>
                                      <Tooltip title="Send Ticket Email">
                                        <IconButton color="primary" size="large" onClick={async () => {
                                          if (!selectedBooking.ticketId) { setSnackbar({ open: true, message: 'No ticket available to send', severity: 'error' }); return; }
                                          try {
                                            const res = await api.post(`/api/tickets/${selectedBooking.ticketId}/send`);
                                            if (res.data && res.data.success) setSnackbar({ open: true, message: 'Ticket emailed successfully', severity: 'success' });
                                            else setSnackbar({ open: true, message: 'Ticket send response', severity: 'info' });
                                          } catch (e) { setSnackbar({ open: true, message: e.response?.data?.error || 'Failed to send ticket', severity: 'error' }); }
                                        }}>
                                          <SendOutlinedIcon fontSize="inherit" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Print Ticket">
                                        <IconButton color="primary" size="large" onClick={() => {
                                          // open preview modal for selected booking
                                          const cat = selectedBooking.packageId?.category || selectedBooking.bookingDetails?.category || 'attendee';
                                          const design = pickDesign(ticketDesigns, cat);
                                          const ticketForPreview = {
                                            type: cat,
                                            attendee: selectedBooking.bookingDetails?.bookerName || selectedBooking.bookingDetails?.companyDetails?.name || '',
                                            reference: selectedBooking.ticketReference || selectedBooking._id,
                                            status: selectedBooking.status,
                                            company: selectedBooking.bookingDetails?.companyDetails?.name || '',
                                            email: selectedBooking.userId?.email || selectedBooking.bookingDetails?.email || '',
                                            phone: selectedBooking.bookingDetails?.mobile || selectedBooking.bookingDetails?.phone || '',
                                            packageName: selectedBooking.packageId?.name || selectedBooking.bookingDetails?.packageName || '',
                                            peopleCount: selectedBooking.bookingDetails?.peopleCount
                                          };
                                          setPrintPreviewData({ design, ticket: ticketForPreview });
                                          setPrintPreviewOpen(true);
                                        }}>
                                          <LocalPrintshopOutlinedIcon fontSize="inherit" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  )}
                                    {/* Payment Request Email Dialog */}
                                    <Modal open={!!paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)}>
                                      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 3, minWidth: 340, maxWidth: 400, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Typography variant="h6" fontWeight={700} gutterBottom color="primary">Send Payment Request Email</Typography>
                                        <Divider sx={{ mb: 2, width: '100%' }} />
                                        <TextField label="Recipient Email" fullWidth sx={{ mb: 2 }} value={paymentEmail} onChange={e => setPaymentEmail(e.target.value)} />
                                        <TextField label="Payment Instructions" fullWidth multiline minRows={3} sx={{ mb: 2 }} value={paymentMsg} onChange={e => setPaymentMsg(e.target.value)} />
                                        <Button variant="contained" color="primary" onClick={handleSendPaymentRequest} disabled={paymentEmailLoading || !paymentEmail} sx={{ width: '100%', fontWeight: 700, mb: 1 }}>{paymentEmailLoading ? 'Sending...' : 'Send Email'}</Button>
                                        <Button onClick={() => setPaymentDialogOpen(false)} sx={{ width: '100%' }}>Cancel</Button>
                                        {paymentEmailResult && <Typography color={paymentEmailResult.includes('success') ? 'success.main' : 'error.main'} sx={{ mt: 2 }}>{paymentEmailResult}</Typography>}
                                      </Box>
                                    </Modal>
                                    {/* Document Request Dialog */}
                                    <Modal open={!!docRequestOpen} onClose={() => setDocRequestOpen(false)}>
                                      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 3, minWidth: 340, maxWidth: 400, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Typography variant="h6" fontWeight={700} gutterBottom color="primary">Request Documents</Typography>
                                        <Divider sx={{ mb: 2, width: '100%' }} />
                                        <TextField label="Message" fullWidth multiline minRows={3} sx={{ mb: 2 }} value={docRequestMsg} onChange={e => setDocRequestMsg(e.target.value)} />
                                        <Button variant="contained" color="primary" onClick={handleSendDocRequest} disabled={docRequestLoading || !docRequestMsg} sx={{ width: '100%', fontWeight: 700, mb: 1 }}>{docRequestLoading ? 'Sending...' : 'Send Request'}</Button>
                                        <Button onClick={() => setDocRequestOpen(false)} sx={{ width: '100%' }}>Cancel</Button>
                                      </Box>
                                    </Modal>
                                  {/* Only superadmin or assigned admin can upload verification */}
                                  {((currentUser.role === 'superadmin') || (currentUser.role === 'admin' && selectedBooking?.assignedStaffId === currentUser._id)) ? (
                                    <Tooltip title="Upload Verification">
                                      <IconButton color="primary" onClick={() => handleUploadVerification(selectedBooking._id)} size="large">
                                        <CloudUploadIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                  ) : null}
                                        {/* Upload Verification Modal */}
                                        <Modal open={!!verificationOpen} onClose={() => setVerificationOpen(false)}>
                                          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 3, minWidth: 340, maxWidth: 400, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <Typography variant="h6" fontWeight={700} gutterBottom color="primary">Upload Payment Verification</Typography>
                                            <Divider sx={{ mb: 2, width: '100%' }} />
                                            {selectedBooking && (
                                              <>
                                                <Typography variant="body1" sx={{ mb: 1 }}><b>Booker:</b> {selectedBooking.bookingDetails?.bookerName || selectedBooking.bookingDetails?.companyDetails?.name || 'N/A'}</Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}><b>Email:</b> {selectedBooking.userId?.email || selectedBooking.bookingDetails?.email || 'N/A'}</Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}><b>Package:</b> {selectedBooking.packageId?.name || selectedBooking.bookingDetails?.packageName || 'N/A'}</Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}><b>Total Payment Needed:</b> ${selectedBooking.packageId?.price || 'N/A'}</Typography>
                                              </>
                                            )}
                                            <Box sx={{ width: '100%', mt: 2 }}>
                                              <Typography variant="body2" sx={{ mb: 1 }}>Payment Notification/Detail</Typography>
                                              <input value={payDetail} onChange={e => setPayDetail(e.target.value)} placeholder="Bank/Mobile Money Message" style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #bbb', fontSize: 15, marginBottom: 8 }} />
                                              {verificationError && <Typography color="error" sx={{ mb: 1 }}>{verificationError}</Typography>}
                                              <Button variant="contained" color="primary" onClick={handleVerificationSubmit} disabled={verifying} sx={{ width: '100%', fontWeight: 700, mt: 1 }}>{verifying ? 'Processing...' : 'Mark as Paid'}</Button>
                                              <Button onClick={() => setVerificationOpen(false)} sx={{ width: '100%', mt: 1 }}>Cancel</Button>
                                            </Box>
                                          </Box>
                                        </Modal>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1, textAlign: 'center' }}>
                                  Select an action for this booking. Actions are permanent.
                                </Typography>
                                <Button onClick={() => setActionModalOpen(false)} sx={{ mt: 2 }}>Close</Button>
                              </Box>
                            </Modal>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            ))}
          </Grid>
        )}
        <NotificationSnackbar
          open={snackbar.open}
          onClose={handleSnackbarClose}
          message={snackbar.message}
          severity={snackbar.severity}
        />
        <PrintPreviewModal open={printPreviewOpen} onClose={() => setPrintPreviewOpen(false)} design={printPreviewData.design} ticket={printPreviewData.ticket} />
        <Modal open={!!modalOpen} onClose={handleCloseModal}>
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 0, borderRadius: 4, minWidth: 340, maxWidth: 500, width: '100%', overflow: 'auto' }}>
            {selectedBooking && (
              <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', width: '100%' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#007bff', mr: 1, fontSize: 18 }}>
                    {selectedBooking.packageId?.name || selectedBooking.bookingDetails?.packageName || selectedBooking.bookingDetails?.name || selectedBooking.bookingDetails?.package || selectedBooking.bookingDetails?.package_name || 'No Package Name'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#555', fontWeight: 700, mr: 1, fontSize: 14 }}>
                    {selectedBooking.packageId?.category || selectedBooking.bookingDetails?.category || selectedBooking.bookingDetails?.type || selectedBooking.bookingDetails?.packageType || selectedBooking.bookingDetails?.package_type || 'No Package Type'}
                  </Typography>
                  {selectedBooking.packageId?.price && (
                    <Typography variant="body2" sx={{ color: '#007bff', fontWeight: 700, mr: 1, fontSize: 14 }}>
                      <span style={{ fontSize: 15, marginRight: 4 }}>💵</span> ${selectedBooking.packageId.price}
                    </Typography>
                  )}
                  {selectedBooking.bookingDetails?.country && (
                    <span title={selectedBooking.bookingDetails.country} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <ReactCountryFlag
                        countryCode={getCountryCode(selectedBooking.bookingDetails.country)}
                        svg
                        style={{ width: '2.5em', height: '2.5em', borderRadius: '5px', boxShadow: '0 0 4px #007bff', border: '2px solid #007bff', verticalAlign: 'middle', marginRight: 8 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#007bff', fontSize: 16 }}>{selectedBooking.bookingDetails.country}</Typography>
                    </span>
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                {/* Details shown only once, in compact layout above */}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  {(!selectedBooking.uploadedDocuments || selectedBooking.uploadedDocuments.length === 0) && (
                    <Tooltip title="Request Document">
                      <IconButton color="warning" size="small" sx={{ borderRadius: 2, boxShadow: '0 2px 8px #ffc10755', fontSize: 18 }} onClick={() => {
                        // TODO: Implement request document email template in mailer
                        alert('TODO: Send request for document email to booker.');
                      }}>
                        <span role="img" aria-label="Request Document" style={{ fontSize: 18 }}>📧</span>
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body1" gutterBottom><b>Name:</b> {selectedBooking.bookingDetails?.bookerName || selectedBooking.bookingDetails?.companyDetails?.name || selectedBooking.userId?.name || selectedBooking.bookingDetails?.companyName || 'N/A'}</Typography>
                    <Typography variant="body1" gutterBottom><b>Email:</b> {selectedBooking.userId?.email || selectedBooking.bookingDetails?.email || 'N/A'}</Typography>
                    <Typography variant="body1" gutterBottom><b>Mobile:</b> {selectedBooking.bookingDetails?.mobile || 'N/A'}</Typography>
                    <Typography variant="body1" gutterBottom><b>Country:</b> {selectedBooking.bookingDetails?.country || 'N/A'}</Typography>
                    <Typography variant="body1" gutterBottom><b>City:</b> {selectedBooking.bookingDetails?.city || 'N/A'}</Typography>
                    <Typography variant="body1" gutterBottom><b>Address:</b> {selectedBooking.bookingDetails?.address || selectedBooking.bookingDetails?.companyDetails?.address || 'N/A'}</Typography>
                    <Typography variant="body1" gutterBottom><b>Company:</b> {selectedBooking.bookingDetails?.companyName || selectedBooking.bookingDetails?.companyDetails?.name || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <b>Status:</b>
                        {selectedBooking.status === 'pending' ? (
                          <span title="Pending" style={{ color: '#d32f2f', fontSize: 20, marginLeft: 4 }}>✔</span>
                        ) : selectedBooking.status === 'approved' ? (
                          <span title="Approved" style={{ color: '#388e3c', fontSize: 20, marginLeft: 4 }}>✔✔</span>
                        ) : (
                          <span style={{ fontWeight: 400, marginLeft: 4 }}>{selectedBooking.status}</span>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <b>Payment:</b>
                        {selectedBooking.paymentStatus === 'pending' ? (
                          <span title="Not Paid" style={{ color: '#d32f2f', fontSize: 20, marginLeft: 4 }}>$</span>
                        ) : selectedBooking.paymentStatus === 'paid' ? (
                          <span title="Paid" style={{ color: '#388e3c', fontSize: 20, marginLeft: 4 }}>$</span>
                        ) : (
                          <span style={{ fontWeight: 400, marginLeft: 4 }}>{selectedBooking.paymentStatus}</span>
                        )}
                      </Box>
                    </Box>
                    <Typography variant="body1" gutterBottom><b>Date:</b> {new Date(selectedBooking.createdAt).toLocaleString()}</Typography>
                    <Typography variant="body1" gutterBottom><b>Bulk Count:</b> {selectedBooking.bookingDetails?.bulkCount || selectedBooking.bookingDetails?.numberOfPeople || 'N/A'}</Typography>
                    <Typography variant="body1" gutterBottom><b>Title:</b> {selectedBooking.bookingDetails?.title || 'N/A'}</Typography>
                    <Typography variant="body1" gutterBottom><b>Accommodation:</b> {selectedBooking.bookingDetails?.accommodation ? 'Yes' : 'No'}</Typography>
                    <Typography variant="body1" gutterBottom><b>Airport Pickup:</b> {selectedBooking.bookingDetails?.airportPickup ? 'Yes' : 'No'}</Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Uploaded Documents</Typography>
                {selectedBooking.uploadedDocuments && selectedBooking.uploadedDocuments.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedBooking.uploadedDocuments.map((doc, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ flex: 1 }}>{doc.name || `Document ${idx + 1}`}</Typography>
                        <Tooltip title="Preview Document"><IconButton color="info" onClick={() => window.open(doc.url, '_blank')}><VisibilityIcon /></IconButton></Tooltip>
                        <Tooltip title="Download Document"><IconButton color="primary" href={doc.url} download><CloudUploadIcon /></IconButton></Tooltip>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary">No documents uploaded.</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Modal>
      </Box>
    </SuperAdminLayout>
  );
}

export default BookingManagement;
